import { QueryClient, useMutation } from "@tanstack/react-query"
import { apiErrorMessage } from "../../../services/apiErrorText"
import { messageError, messageSuccess } from "../../../services/messages"
import {
    addStopToTour,
    createTour,
    deleteTour,
    moveStopOrder,
    moveStopToOtherTour,
    moveStopToWeekday,
    removeStopFromTour,
    updateTour,
} from "./apiCalls"
import { TourObjectRef, TourStop, TourWeekday } from "../types"

const refresh = (queryClient: QueryClient) => {
    queryClient.invalidateQueries({ predicate: (query) => String(query.queryKey[0] ?? "").startsWith("tour") })
}

export const useCreateTourMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (name: string) => createTour(name),
        onSuccess: () => {
            refresh(queryClient)
            messageSuccess("tour-create", "Tour angelegt", "Die Tour wurde gespeichert.")
        },
        onError: (err) => {
            messageError("tour-create", "Fehler!", apiErrorMessage(err, "Die Tour konnte nicht angelegt werden."))
        },
    })

export const useUpdateTourMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (data: { id: string; name: string; active: boolean; employee?: string | null }) =>
            updateTour(data.id, { name: data.name, active: data.active, employee: data.employee }),
        onSuccess: () => {
            refresh(queryClient)
            messageSuccess("tour-update", "Tour gespeichert", "Name, Mitarbeiter und Status wurden übernommen.")
        },
        onError: (err) => {
            messageError("tour-update", "Fehler!", apiErrorMessage(err, "Die Tour konnte nicht gespeichert werden."))
        },
    })

export const useDeleteTourMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (id: string) => deleteTour(id),
        onSuccess: () => {
            refresh(queryClient)
            messageSuccess("tour-delete", "Tour entfernt", "Die Tour wurde gelöscht.")
        },
        onError: (err) => {
            messageError("tour-delete", "Fehler!", apiErrorMessage(err, "Die Tour konnte nicht gelöscht werden."))
        },
    })

export const useAddTourStopMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (data: { tourId: string; object: TourObjectRef; weekday: TourWeekday; extra?: Partial<TourStop> }) =>
            addStopToTour(data.tourId, data.object, data.weekday, data.extra),
        onSuccess: () => {
            refresh(queryClient)
            messageSuccess("tour-stop-add", "Objekt zugeordnet", "Das Objekt liegt auf dem gewählten Wochentag.")
        },
        onError: (err) => {
            messageError("tour-stop-add", "Fehler!", apiErrorMessage(err, "Das Objekt konnte der Tour nicht zugeordnet werden."))
        },
    })

export const useRemoveTourStopMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (data: { tourId: string; stopId: string }) => removeStopFromTour(data.tourId, data.stopId),
        onSuccess: () => refresh(queryClient),
        onError: (err) => {
            messageError("tour-stop-remove", "Fehler!", apiErrorMessage(err, "Das Objekt konnte nicht entfernt werden."))
        },
    })

export const useReorderTourStopMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (data: { tourId: string; stopId: string; direction: -1 | 1 }) => moveStopOrder(data.tourId, data.stopId, data.direction),
        onSuccess: () => refresh(queryClient),
    })

export const useMoveTourStopWeekdayMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (data: { tourId: string; stopId: string; weekday: TourWeekday }) => moveStopToWeekday(data.tourId, data.stopId, data.weekday),
        onSuccess: () => refresh(queryClient),
    })

export const useMoveTourStopToTourMutation = (queryClient: QueryClient) =>
    useMutation({
        mutationFn: async (data: { fromTourId: string; toTourId: string; stopId: string; weekday: TourWeekday }) =>
            moveStopToOtherTour(data.fromTourId, data.toTourId, data.stopId, data.weekday),
        onSuccess: () => {
            refresh(queryClient)
            messageSuccess("tour-move", "Objekt verschoben", "Das Objekt liegt jetzt auf der anderen Tour.")
        },
        onError: (err) => {
            messageError("tour-move", "Fehler!", apiErrorMessage(err, "Das Objekt konnte nicht verschoben werden."))
        },
    })
